import {put, call, all} from 'redux-saga/effects';
import Address from "../../entities/address";
import * as Actions from "../actions/addressActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type AddressAction = {
    addressId: string;
    address: Address;
    addresses: Address[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneAddress(action: AddressAction) {
	const session = tabSession.captureGeneration();
	try {
		const address: Address = yield call(api.findOne, 'address', action.addressId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_FETCH_ONE_SUCCESS, addressId: action.addressId, address, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ADDRESS_FETCH_ONE_FAILURE, addressId: action.addressId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {AddressAction} action
 */
export function* saveAddress(action: AddressAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ADDRESS_SAVE saga', action.address?.id);

		if (action.address === undefined || action.address === null) {
			console.error('saveAddress received a null');
			throw new Error('saveAddress received a null');
		}

		if (!(action.address instanceof Address)) {
			action.address = new Address(action.address);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.address.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.address);
			const addResult = yield call(idb.add, 'address', action.address);
			if (addResult.isErr()) {
				console.error(`⮂ saveAddress: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertAddressSave(action.address, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ADDRESS_SAVE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ADDRESS_SAVE, address: action.address, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertAddressSave(action.address, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ADDRESS_SAVE_FAILURE, address: action.address, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {AddressAction} action
 * @param {Address[]} action.addresses
 */
export function* saveManyAddresses(action: AddressAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ADDRESS_SAVE_MANY saga', action.addresses.map(address => address.id).join(','));

        if (!Array.isArray(action.addresses)) {
            console.error('saveManyAddresses expected an array, received', action.addresses);
            throw new Error('saveManyAddresses expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveAddress), under these queue keys
		const dispatchedAddedOns: number[] = action.addresses.map(address => address?.addedOn);
		const revertAll = () => action.addresses.map((address, i) =>
			put(Actions.revertAddressSave(address, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Address[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.addresses.length; i++) {
				const address = action.addresses[i];
				address.persisted = false;
				ensureOperationId(address);
				toSave.push(address);
			}

			const addResult = yield call(idb.add, 'address', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyAddresses: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ADDRESS_SAVE_MANY_FAILURE, addresses: action.addresses, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ADDRESS_SAVE_MANY, addresses: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ADDRESS_SAVE_MANY_FAILURE, addresses: action.addresses, status: 500, err});

			console.error(`Error saving ${action.addresses.length} addresses to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {AddressAction} action
 */
export function* removeAddress(action: AddressAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.address === undefined || action.address === null || !(action.address instanceof Address)) {
			console.error('removeAddress expected type Address, received', action.address);
			reportQueued(false);
			throw new Error('removeAddress expected type Address');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.address.status = 'D';
		action.address.addedOn = newHRTimestamp();
		action.address.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.address);
		const addResult = yield call(idb.add, 'address', action.address);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeAddress: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ADDRESS_REMOVE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_REMOVE, address: action.address});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ADDRESS_REMOVE_FAILURE, address: action.address, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Address.
	*
	* @param {AddressAction} action
	*/
export function* restoreAddress(action: AddressAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreAddress expected getCurrentUserId() to return a value');
        return;
    }

	if (action.address === undefined || action.address === null) {
        console.error('restoreAddress expected a Address, received null');
        throw new Error('restoreAddress expected a Address, received null');
    }
    else if (!(action.address instanceof Address)) action.address = new Address(action.address); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.address.status = 'U';
		action.address.addedOn = newHRTimestamp();
		action.address.addedBy = userId;
		action.address.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.address);
		const addResult = yield call(idb.add, 'address', action.address);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreAddress: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ADDRESS_RESTORE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_RESTORE, address: action.address});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ADDRESS_RESTORE_FAILURE, address: action.address, status: 500, err});

		console.error('Error restoring from database', err);
	}
}